import { FC, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import classNames from 'classnames'
import moment from 'moment'
import Button from 'src/components/Button'
import { Skeleton } from 'src/components/Skeleton'
import { ReactComponent as CloseIcon } from 'src/assets/svg/close.svg'
import { ReactComponent as ExternalLinkIcon } from 'src/assets/svg/external-link.svg'
import { ReactComponent as RepostsIcon } from 'src/assets/svg/reposts.svg'
import { ReactComponent as SharesIcon } from 'src/assets/svg/shares.svg'
import { SOCIAL_ICONS } from 'src/constants/socialIcons'
import { SENTIMENT_COLORS } from 'src/constants/sentimentColors'
import { DEFAULT_ORIGINAL_LANG_CODE } from 'src/constants/defaultOriginalLangCode'
import en from 'src/constants/en'
import { numberWithCommas } from 'src/utils/numberFormat'
import { CampaignComment } from 'src/reactQuery/useGetCampaignCommentsQuery'
import { useGetThreadDetailsQuery } from 'src/reactQuery/useGetThreadDetailsQuery'
import { buildCommentTree, CommentThread } from 'src/utils/commentTree'
import { getMentionByline } from 'src/utils/mentionByline'
import styles from './mentionDetailsModal.module.scss'

type SentimentTab = 'all' | 'positive' | 'neutral' | 'negative'

const commentTime = (comment: CampaignComment) =>
  comment.timestamp ? new Date(comment.timestamp).getTime() : 0

const CommentCard: FC<{
  comment: CampaignComment
  replyTo?: CampaignComment | null
}> = ({ comment, replyTo }) => {
  const sentiment = comment.sentiment || 'neutral'
  return (
    <div className={styles.commentItem}>
      {replyTo && (
        <span className={styles.replyTo}>↳ Reply to {replyTo.author}</span>
      )}
      <div className={styles.commentHeader}>
        <span className={styles.commentAuthor}>{comment.author}</span>
        <span
          className={styles.sentimentBadge}
          style={{
            backgroundColor: SENTIMENT_COLORS[sentiment] + '20',
            color: SENTIMENT_COLORS[sentiment],
          }}
        >
          {sentiment.charAt(0).toUpperCase() + sentiment.slice(1)}
        </span>
      </div>
      <p className={styles.commentContent}>{comment.content}</p>
      {comment.timestamp && (
        <span className={styles.commentTimestamp}>
          {moment(comment.timestamp).format('D MMM YYYY, HH:mm')}
        </span>
      )}
    </div>
  )
}

/** A comment and, indented beneath it, its replies (recursively). */
const ThreadedComment: FC<{ thread: CommentThread<CampaignComment> }> = ({
  thread,
}) => (
  <div className={styles.thread}>
    <CommentCard comment={thread.item} replyTo={thread.replyTo} />
    {thread.replies.length > 0 && (
      <div className={styles.replies}>
        {thread.replies.map((reply) => (
          <ThreadedComment key={reply.item.id} thread={reply} />
        ))}
      </div>
    )}
  </div>
)

interface MentionDetailsModalProps {
  onClose: () => void
  threadId: string | null
}

/**
 * Post Details for a social-listening mention: the mention text, a link to
 * the post, and its comments — the modal Content Analysis opens from a post
 * card, minus the reactions/stats row, fed by /analytics/fetchThreadDetails
 * instead of an embedded comments array. Shares and reposts (Instagram) are
 * shown when the scraped post reported them (2026-10); nothing when unknown.
 */
export const MentionDetailsModal: FC<MentionDetailsModalProps> = ({
  onClose,
  threadId,
}) => {
  const [activeTab, setActiveTab] = useState<SentimentTab>('all')
  const [isTranslated, setIsTranslated] = useState(false)

  const { data, isError, isLoading } = useGetThreadDetailsQuery(threadId)

  const post = data?.post
  const comments = useMemo(() => data?.comments || [], [data])

  // Threads: top-level comments newest first, replies nested under their
  // parent oldest first. On a sentiment tab, a matching reply whose parent
  // does not match is shown at top level with "Reply to <author>".
  const commentThreads = useMemo(() => {
    const visible =
      activeTab === 'all'
        ? comments
        : comments.filter((comment) => comment.sentiment === activeTab)

    return buildCommentTree(
      visible,
      {
        id: (comment) => comment.commentId,
        parentId: (comment) => comment.parentCommentId,
        time: commentTime,
      },
      comments
    )
  }, [comments, activeTab])

  const handleClose = () => {
    setActiveTab('all')
    setIsTranslated(false)
    onClose()
  }

  if (!threadId) return null

  const canTranslate =
    !!post?.translated && post.original_lang_code !== DEFAULT_ORIGINAL_LANG_CODE
  const text = isTranslated && post?.translated ? post.translated : post?.text
  const platform = (post?.source || '').toLowerCase()
  const byline =
    post &&
    getMentionByline({ authorName: post.author, sourceName: post.source_name })

  return createPortal(
    <>
      <div className={styles.backdrop} onClick={handleClose} />
      <div className={styles.modal}>
        <div className={styles.header}>
          <h2 className={styles.title}>Post Details</h2>
          <button
            className={styles.closeButton}
            onClick={handleClose}
            type="button"
          >
            <CloseIcon />
          </button>
        </div>

        <div className={styles.content}>
          {isError || (!isLoading && !post) ? (
            <div className={styles.emptyComments}>
              This mention could not be loaded.
            </div>
          ) : isLoading || !post ? (
            <>
              <Skeleton height={22} width="90%" />
              <Skeleton height={22} width="70%" />
              <div style={{ height: 16 }} />
              <Skeleton height={20} width={260} />
            </>
          ) : (
            <>
              {byline && <h3 className={styles.author}>{byline}</h3>}
              <p className={styles.postContent}>{text}</p>
              {(typeof post.shares === 'number' ||
                typeof post.reposts === 'number') && (
                <div className={styles.postMetrics}>
                  {typeof post.shares === 'number' && (
                    <span className={styles.postMetric} title="Shares">
                      <SharesIcon className={styles.postMetricIcon} />
                      {numberWithCommas(post.shares)}
                    </span>
                  )}
                  {typeof post.reposts === 'number' && (
                    <span className={styles.postMetric} title="Reposts">
                      <RepostsIcon className={styles.postMetricIcon} />
                      {numberWithCommas(post.reposts)}
                    </span>
                  )}
                </div>
              )}
              <div className={styles.postActions}>
                {canTranslate && (
                  <button
                    className={styles.translateButton}
                    onClick={() => setIsTranslated((v) => !v)}
                    type="button"
                  >
                    {isTranslated ? en.seeOriginal : en.seeTranslation}
                  </button>
                )}
                <a
                  className={styles.seePostButton}
                  href={post.link || '#'}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  <span className={styles.platformIcon}>
                    {SOCIAL_ICONS[platform]}
                  </span>
                  <span className={styles.seePostText}>See post</span>
                  <ExternalLinkIcon className={styles.externalIcon} />
                </a>
              </div>
            </>
          )}

          <div className={styles.commentsSection}>
            <div className={styles.commentsHeader}>
              <span className={styles.commentsTitle}>
                Comments
                {!!data && ` (${numberWithCommas(data.totalComments)})`}
              </span>
              <div className={styles.tabs}>
                {(
                  ['all', 'positive', 'neutral', 'negative'] as SentimentTab[]
                ).map((tab) => (
                  <button
                    className={classNames(styles.tab, {
                      [styles.active]: activeTab === tab,
                    })}
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    type="button"
                  >
                    {tab.charAt(0).toUpperCase() + tab.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            <div className={styles.commentsList}>
              {isLoading ? (
                <>
                  <Skeleton height={64} width="100%" />
                  <Skeleton height={64} width="100%" />
                </>
              ) : commentThreads.length === 0 ? (
                <div className={styles.emptyComments}>No comments found</div>
              ) : (
                commentThreads.map((thread) => (
                  <ThreadedComment key={thread.item.id} thread={thread} />
                ))
              )}
              {data?.truncated && (
                <div className={styles.emptyComments}>
                  Showing the newest {comments.length} of{' '}
                  {numberWithCommas(data.totalComments)} comments
                </div>
              )}
            </div>
          </div>
        </div>

        <div className={styles.footer}>
          <Button background="transparent" onClick={handleClose}>
            Cancel
          </Button>
          <Button background="malibu" onClick={handleClose}>
            Done
          </Button>
        </div>
      </div>
    </>,
    document.body
  )
}
